import { useMemo } from "react";

import { useAuth } from "@/app/context/Auth/AuthContext";
import { type TierName, useSubscription } from "@/app/hooks/useSubscription";

import { useFitCheckUsage } from "./api";

const WINDOW_DAYS = 30;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

// Per rolling 30 days. `Infinity` means unlimited. Free (Bronze) gets a small taste; any paid tier
// lifts FitChecks entirely and widens tailoring as it climbs.
const FITCHECK_LIMITS: Record<TierName, number> = {
	BRONZE: 3,
	SILVER: Infinity,
	GOLD: Infinity,
	PLATINUM: Infinity,
};

const TAILOR_LIMITS: Record<TierName, number> = {
	BRONZE: 1,
	SILVER: 5,
	GOLD: 20,
	PLATINUM: Infinity,
};

export interface Allowance {
	limit: number;
	used: number;
	remaining: number;
	unlimited: boolean;
	/** When at or over the limit, the moment the next credit frees up. Null while credits remain. */
	nextResetAt: Date | null;
}

// `timestamps` are the counted actions, oldest first. Each leaves the window WINDOW_MS after it
// happened. The first usable credit returns when enough of the oldest have aged out to drop the
// count below the limit — that is the item at index (used - limit).
function toAllowance(limit: number, timestamps: string[]): Allowance {
	const unlimited = !Number.isFinite(limit);
	const used = timestamps.length;
	const remaining = unlimited ? Infinity : Math.max(0, limit - used);
	const nextResetAt =
		!unlimited && used >= limit ? new Date(new Date(timestamps[used - limit]).getTime() + WINDOW_MS) : null;

	return { limit, used, remaining, unlimited, nextResetAt };
}

export interface FitCheckAllowances {
	fitCheck: Allowance;
	tailor: Allowance;
	tier: TierName;
	isLoading: boolean;
}

/**
 * What the signed-in user may still do this window, per their tier and recent usage.
 *
 * The tier comes from `useSubscription` and the counts from `useFitCheckUsage`, so this is the one
 * place the gate and the credits meter both read.
 */
export function useFitCheckAllowances(): FitCheckAllowances {
	const { userData } = useAuth();
	const userId = userData?.userID || "";
	const { tier, isLoading: subLoading } = useSubscription();
	const { data: usage, isLoading: usageLoading } = useFitCheckUsage(userId);

	return useMemo(() => {
		const fitTimestamps = usage?.fitChecks.timestamps ?? [];
		const tailorTimestamps = usage?.tailoredResumes.timestamps ?? [];
		return {
			fitCheck: toAllowance(FITCHECK_LIMITS[tier], fitTimestamps),
			tailor: toAllowance(TAILOR_LIMITS[tier], tailorTimestamps),
			tier,
			isLoading: subLoading || usageLoading,
		};
	}, [usage, tier, subLoading, usageLoading]);
}
